import { memo, type ReactElement } from "react";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import {
  formatBatchFailuresCount,
  formatBatchReceivedTotal,
} from "../helpers/formatBatchTaskDisplay";
import type { FluxZapBatchTaskView } from "../types";

export type BatchTaskMetricKind = "received" | "failures";

export interface BatchTaskMetricCellProps {
  task: FluxZapBatchTaskView;
  kind: BatchTaskMetricKind;
}

const metricValueSx = {
  fontVariantNumeric: "tabular-nums" as const,
  fontWeight: 700,
  fontSize: "1.0625rem",
  lineHeight: 1.2,
  color: "text.primary",
};

const metricIconSx = {
  fontSize: "1.125rem",
  color: "error.main",
};

const metricCaptionSx = {
  fontSize: "0.6875rem",
  lineHeight: 1.2,
  color: "text.secondary",
  letterSpacing: "0.02em",
};

function parseMetricValue(value: string): number | null {
  if (value === "—") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function ReceivedMetric({ value }: { value: string }) {
  const numeric = parseMetricValue(value);
  const isIndeterminate = numeric === null;

  return (
    <Stack alignItems="center" spacing={0.35} aria-label={`${value} recebidos`}>
      <Typography component="span" sx={metricValueSx}>
        {value}
      </Typography>
      {!isIndeterminate ? (
        <Typography component="span" sx={metricCaptionSx}>
          recebidos
        </Typography>
      ) : null}
    </Stack>
  );
}

function FailuresMetric({ value }: { value: string }) {
  const numeric = parseMetricValue(value);
  const isIndeterminate = numeric === null;
  const hasFailures = numeric !== null && numeric > 0;
  const isClear = numeric === 0;

  let icon: ReactElement | null = null;
  if (hasFailures) {
    icon = <BlockOutlinedIcon sx={metricIconSx} aria-hidden />;
  }

  return (
    <Stack
      alignItems="center"
      spacing={0.35}
      aria-label={
        isIndeterminate
          ? "Falhas indisponíveis"
          : hasFailures
            ? `${value} falhas`
            : "Nenhuma falha"
      }
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          px: hasFailures ? 1 : 0,
          py: hasFailures ? 0.35 : 0,
          borderRadius: 1,
          border: hasFailures ? "1px dashed" : "none",
          borderColor: "divider",
          bgcolor: hasFailures ? "action.hover" : "transparent",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={0.75}>
          {icon}
          <Typography component="span" sx={metricValueSx}>
            {value}
          </Typography>
        </Stack>
      </Box>
      {isClear ? (
        <Typography component="span" sx={metricCaptionSx}>
          nenhuma falha
        </Typography>
      ) : hasFailures ? (
        <Typography component="span" sx={metricCaptionSx}>
          não entregues
        </Typography>
      ) : null}
    </Stack>
  );
}

const BatchTaskMetricCell = memo(({ task, kind }: BatchTaskMetricCellProps) => {
  const value =
    kind === "received"
      ? formatBatchReceivedTotal(task)
      : formatBatchFailuresCount(task);

  if (kind === "received") {
    return <ReceivedMetric value={value} />;
  }

  return <FailuresMetric value={value} />;
});

BatchTaskMetricCell.displayName = "BatchTaskMetricCell";

export default BatchTaskMetricCell;
